import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, TextField, Button, Chip, Alert, CircularProgress, Tooltip,
    ToggleButton, ToggleButtonGroup, MenuItem,
} from '@mui/material';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    StateCredentialRow, StateLoginOption, FilingLoginDefault, getPartnerCredentials, getStateLoginOptions,
    getFilingLoginDefaults, getSystemLoginOptions, setFilingLoginDefault, approveFilingLoginDefault,
    removeFilingLoginDefault, saveCredential, approveCredential, revokeCredential, removeCredential,
    verifyCredential, VerifyCredentialData, defaultLastVerified,
} from '../../helper/stateFilingCredentials';

/**
 * Per-client API-credential editor for the Partners screen (Jerry 2026-07-22). One write-only field per
 * state, grouped by filing system, driven entirely by each state's contract so there is no per-state
 * branching here: a Client-login state shows an API key (+ username where the system uses one), a
 * MISC-login state shows the client's identifier plus a read-only line for the shared MISC login.
 *
 * The key is NEVER shown back — the server never returns it; a set value renders only as "Set on
 * <date>". Saving a new/replaced credential lands it UNAPPROVED (cannot file until approved). Approve,
 * revoke and remove are explicit, audited actions. The whole panel is admin-gated by the caller.
 */

const BLUE = '#2099d2';
const BLUE_HOVER = '#176990';
const SLATE_500 = '#475569';
const SLATE_400 = '#64748b';
const BORDER = '#e2e8f0';
const GREEN = '#1e7e34';
const AMBER = '#b57614';
const RED = '#c0453b';

interface Props {
    token?: string;
    partnersId: number;
    partnerName?: string;
    /** Only admins may edit; a non-admin caller should not render this panel at all, but when false the
     *  fields/buttons render read-only as a defence in depth. */
    canManage?: boolean;
}

function fmtDate(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function fmtTime(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Past this many days a "last known good" is old enough that it should not be read as "works today". */
const VERIFY_STALE_DAYS = 30;

function daysSince(iso: string): number | null {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86_400_000);
}

/** "Verified 28 Jul" — same-year dates drop the year, older ones keep it so a year-old check can't read
 *  as a recent one. */
function fmtVerifiedOn(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Verified';
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return `Verified ${d.toLocaleDateString([], sameYear
        ? { day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export function StateFilingCredentialsPanel({ token, partnersId, canManage = true }: Props) {
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false);
    const [rows, setRows] = useState<StateCredentialRow[]>([]);
    const [defaults, setDefaults] = useState<Map<string, FilingLoginDefault>>(new Map());
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!token || !partnersId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            const [res, defs] = await Promise.all([
                getPartnerCredentials(token, partnersId),
                getFilingLoginDefaults(token, partnersId),
            ]);
            if (cancelled) return;
            setRows(res.rows);
            setDefaults(new Map(defs.map((d) => [d.filingSystem, d])));
            setUnavailable(res.unavailable);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [token, partnersId, reloadKey]);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    // Group by filing system, preserving the server's (system, name) ordering.
    const groups = useMemo(() => {
        const bySystem = new Map<string, StateCredentialRow[]>();
        for (const r of rows) {
            const key = r.filingSystemLabel || r.filingSystem;
            if (!bySystem.has(key)) bySystem.set(key, []);
            bySystem.get(key)!.push(r);
        }
        return Array.from(bySystem.entries());
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
                <CircularProgress size={22} sx={{ color: BLUE }} />
                <Typography sx={{ color: SLATE_400, fontSize: '14px' }}>Loading filing credentials…</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {unavailable && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '13px', borderRadius: '10px' }}>
                    The filing-credentials service is still being deployed. This section will activate once the
                    backend is live.
                </Alert>
            )}
            {rows.length === 0 && !unavailable && (
                <Typography sx={{ fontSize: '13px', color: SLATE_400 }}>No filing-enabled states configured.</Typography>
            )}

            {groups.map(([systemLabel, groupRows]) => {
                const scope = groupRows[0]?.credentialOwnerScope;
                const filingSystem = groupRows[0]?.filingSystem;
                // A default login is offered for a Client-login system that spans more than one state.
                const showDefault = scope === 'Client' && groupRows.length > 1;
                const def = filingSystem ? defaults.get(filingSystem) : undefined;
                const hasDefault = showDefault && !!def;
                const defVerifiedAt = hasDefault ? defaultLastVerified(def) : null;
                // Which state we probe when testing the SYSTEM default. The verify endpoint is addressed
                // by state and resolves whatever credential that state would actually file with, so the
                // probe has to be a state that INHERITS the default — probing a state that overrides it
                // would silently test the override and report it as the default's health.
                const inheritsDefaultRow = (r: StateCredentialRow) =>
                    r.credentialOwnerScope !== 'Misc' && !r.credHasSecret && r.linkedCredentialId == null;
                const defaultProbeState = hasDefault
                    ? (groupRows.find((r) => r.supportsVerify && inheritsDefaultRow(r))?.postalAbbv ?? null)
                    : null;
                return (
                    <Box key={systemLabel} sx={{ mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>{systemLabel}</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: SLATE_400 }}>
                                {showDefault ? 'One login covers these states — override a state only if it differs' : (groupRows[0]?.requiredIdentifierLabel ? 'This client’s own login plus its identifier' : 'This client’s own login')}
                            </Typography>
                            <Typography sx={{ fontSize: '11.5px', color: SLATE_400, ml: 'auto' }}>
                                {groupRows.map((r) => r.postalAbbv).join(' · ')}
                            </Typography>
                        </Box>
                        {showDefault && filingSystem && (
                            <SystemDefault
                                token={token} partnersId={partnersId} canManage={canManage}
                                filingSystem={filingSystem} systemLabel={systemLabel} def={def} onChanged={reload}
                                verifiedAt={defVerifiedAt} probeState={defaultProbeState}
                                coverage={groupRows.map((r) => r.postalAbbv).join(' · ')}
                            />
                        )}
                        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                            {groupRows.map((r, i) => (
                                <CredentialRow
                                    key={r.stateId}
                                    row={r}
                                    token={token}
                                    partnersId={partnersId}
                                    canManage={canManage}
                                    onChanged={reload}
                                    first={i === 0}
                                    hasDefault={hasDefault}
                                    defaultUsername={hasDefault ? (def?.loginUsername ?? null) : null}
                                    defaultApproved={hasDefault ? !!def?.approvedForSubmit : false}
                                    defaultVerifiedAt={defVerifiedAt}
                                />
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── one state row ──────────────────────────────────────────────────────────────────────────────────
interface RowProps {
    row: StateCredentialRow;
    token?: string;
    partnersId: number;
    canManage: boolean;
    onChanged: () => void;
    first: boolean;
    /** The system has a default login set (SLIP+). Keyed off the default's EXISTENCE, not its username —
     *  a default with a blank username still covers the state, and treating it as absent used to drop the
     *  state back into the full editor as if nothing were configured. */
    hasDefault?: boolean;
    /** When the system has a default login and this state hasn't overridden it, the row collapses to
     *  "Using default (username)" until the admin chooses to override. */
    defaultUsername?: string | null;
    /** The default's approval + last-known-good, so an inheriting state can REFLECT the default's real
     *  state instead of claiming it is unconfigured. */
    defaultApproved?: boolean;
    defaultVerifiedAt?: string | null;
}

function CredentialRow({
    row, token, partnersId, canManage, onChanged, first,
    hasDefault = false, defaultUsername, defaultApproved = false, defaultVerifiedAt = null,
}: RowProps) {
    const isMisc = row.credentialOwnerScope === 'Misc';
    const hasApiKey = !isMisc && row.credHasSecret;
    const hasLinked = !isMisc && row.linkedCredentialId != null;
    const hasOwnLogin = hasApiKey || hasLinked;
    // Inheriting the system default: no per-state login of its own, and a default exists.
    const [overriding, setOverriding] = useState(false);
    const inheritsDefault = !isMisc && !hasOwnLogin && hasDefault && !overriding;

    // The "active" credential for this row: the identifier (MISC), the API key, or the linked login —
    // whichever exists. Approve / revoke / remove act on it.
    const credentialId = isMisc ? row.identifierId : (row.credentialId ?? row.linkedCredentialId);
    const hasValue = isMisc ? row.identHasValue : (hasApiKey || hasLinked);
    const approved = isMisc ? row.identApproved : (hasLinked ? row.linkedApproved : row.credApproved);
    const approvedByName = isMisc ? row.identApprovedByName : (hasLinked ? row.linkedApprovedByName : row.credApprovedByName);
    // A test button belongs WHERE THE CREDENTIAL LIVES (Jerry 2026-07-28: "for SLIP+ I only want to test
    // the default, not each state"). One SLIP+ login covers CO/LA/SD/TN, so four per-state buttons meant
    // four identical authentications against the same credential — and four chances to trip the server's
    // 1/min rate limit. So: test only when this state has SOMETHING OF ITS OWN to test. A state that
    // inherits the system default shows the DEFAULT's verdict and the button lives on the default row.
    //
    // This is not a blanket "one test per system" rule: FSLSO/SLAcal/SLTX credentials genuinely are
    // per-state (CA and TX carry their own login plus an identifier), so those rows keep their own
    // button — they fall out of this condition naturally, since they each have their own value.
    const canVerify = row.supportsVerify && hasValue && !inheritsDefault;
    const setAt = isMisc ? row.identSetAt : row.credSecretSetAt;
    const lastUsedAt = isMisc ? row.identLastUsedAt : (hasLinked ? row.linkedLastUsedAt : row.credLastUsedAt);

    // Client-scope credential mode: an API key or a linked login (Jerry 2026-07-24). Default to whatever
    // exists; a fresh row defaults to linking a login (the common case for SLIP states).
    type Mode = 'key' | 'link';
    const [mode, setMode] = useState<Mode>(hasApiKey ? 'key' : 'link');

    // Local edit state. The secret is NEVER seeded from the server (it never sends one).
    const [username, setUsername] = useState(row.credUsername ?? '');
    const [secret, setSecret] = useState('');
    const [replacing, setReplacing] = useState(!hasValue);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    // Linked-login dropdown: options for this client + state, lazy-loaded when link mode is shown.
    const [options, setOptions] = useState<StateLoginOption[] | null>(null);
    const [selectedLoginId, setSelectedLoginId] = useState<number | ''>('');
    useEffect(() => {
        if (isMisc || mode !== 'link' || options !== null || !token) return;
        let cancelled = false;
        (async () => {
            const opts = await getStateLoginOptions(token, partnersId, row.stateId);
            if (!cancelled) setOptions(opts);
        })();
        return () => { cancelled = true; };
    }, [isMisc, mode, options, token, partnersId, row.stateId]);

    const usernameChanged = mode === 'key' && !isMisc && row.usesUsername && username.trim() !== (row.credUsername ?? '').trim();
    const secretEntered = secret.trim() !== '';
    const linkSelected = mode === 'link' && selectedLoginId !== '' && selectedLoginId !== row.linkedStateLoginsId;
    const canSave = canManage && !busy && (
        isMisc ? secretEntered
            : (mode === 'key' ? (secretEntered || usernameChanged) : linkSelected)
    );

    // ── Connection test (independent of approval) ──────────────────────────────────────────────────
    // Answers "does this stored credential authenticate against the state?", which is a different
    // question from "has an admin approved it to file?" — a row can be Approved but broken, or reachable
    // but unapproved.
    //
    // The VERDICT IS NOT EPHEMERAL. `cred.LastVerifiedAt` already persists the last successful check and
    // the list endpoint already returns it as `credLastVerifiedAt`; the panel used to fetch it and then
    // render only click-local state, so a credential verified last week came back reading as never
    // tested. The last-known-good below is therefore SEEDED FROM THE SERVER and only overridden by a
    // fresh success in this session. Nothing here triggers a verify — it is a click-only call.
    const [verifying, setVerifying] = useState(false);
    /** A success in THIS session, which outranks the server's (older) copy until the next reload. */
    const [freshGoodAt, setFreshGoodAt] = useState<string | null>(null);
    /** The full payload of the most recent test in this session — connected or failed. */
    const [lastOutcome, setLastOutcome] = useState<VerifyCredentialData | null>(null);
    const [verifyNote, setVerifyNote] = useState<{ tone: 'warn' | 'err'; text: string } | null>(null);
    const [cooldownLeft, setCooldownLeft] = useState(0);
    // A save/replace/remove makes the server's stored timestamp describe the OLD credential, so suppress
    // it until the reload lands with the row the mutation produced.
    const [mutatedSinceLoad, setMutatedSinceLoad] = useState(false);
    useEffect(() => { setMutatedSinceLoad(false); }, [row]);

    // An inheriting state has no verdict of its own — it shows the default's.
    const persistedGoodAt = inheritsDefault ? defaultVerifiedAt : row.credLastVerifiedAt;
    const lastGoodAt = freshGoodAt ?? (mutatedSinceLoad ? null : persistedGoodAt ?? null);
    const failure = lastOutcome && lastOutcome.status !== 'connected' ? lastOutcome : null;

    // Countdown for a server-imposed cooldown. The rate limit itself lives on the server (1/min per
    // client + state); this only mirrors the retryAfterSeconds it hands back so the button re-enables.
    useEffect(() => {
        if (cooldownLeft <= 0) return;
        const t = setTimeout(() => setCooldownLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldownLeft]);

    const runVerify = useCallback(async () => {
        setVerifying(true); setVerifyNote(null);
        const res = await verifyCredential(token, partnersId, row.postalAbbv);
        setVerifying(false);
        if (!res.ok || !res.data) {
            setVerifyNote({ tone: 'err', text: res.message || 'Could not reach the filing system — try again.' });
            return;
        }
        if (res.data.status === 'cooldown') {
            // Neutral: a cooldown says nothing about the credential, so leave the last verdict standing.
            setVerifyNote({ tone: 'warn', text: res.data.message });
            setCooldownLeft(Math.max(0, Math.ceil(res.data.retryAfterSeconds ?? 0)));
            return;
        }
        setLastOutcome(res.data);
        // Only a success moves the last-known-good marker — mirroring the server, which stamps
        // LastVerifiedAt on success and leaves it alone on failure. A failure is surfaced separately so
        // it never reads as having erased a real earlier verification.
        if (res.data.status === 'connected') setFreshGoodAt(res.data.checkedAt);
    }, [token, partnersId, row.postalAbbv]);

    const run = async (fn: () => Promise<{ ok: boolean; message?: string }>, okText: string, after?: () => void) => {
        setBusy(true); setMsg(null);
        const res = await fn();
        setBusy(false);
        if (res.ok) {
            setSecret(''); setReplacing(false); setConfirmRemove(false); setSelectedLoginId('');
            // Any mutation invalidates the last connection verdict — it was about the previous credential.
            setFreshGoodAt(null); setLastOutcome(null); setVerifyNote(null); setMutatedSinceLoad(true);
            setMsg({ tone: 'ok', text: okText });
            onChanged();
            if (after) after();
        } else {
            setMsg({ tone: 'err', text: res.message || 'Request failed — try again.' });
        }
    };

    const handleSave = () => run(() => {
        if (isMisc) return saveCredential(token, { partnersId, state: row.postalAbbv, value: secret, label: null });
        if (mode === 'link') return saveCredential(token, { partnersId, state: row.postalAbbv, stateLoginsId: Number(selectedLoginId), secret: secret.trim() || undefined });
        return saveCredential(token, { partnersId, state: row.postalAbbv, secret, username: row.usesUsername ? (username.trim() || null) : null });
    }, 'Saved. Awaiting approval before it can file.');

    // Approving is the one place a test fires without a click: once a credential is cleared to file, an
    // admin should immediately see whether it actually authenticates.
    const handleApprove = () => credentialId && run(() => approveCredential(token, credentialId), 'Approved for submit.', canVerify ? runVerify : undefined);
    const handleRevoke = () => credentialId && run(() => revokeCredential(token, credentialId), 'Approval revoked.');
    const handleRemove = () => credentialId && run(() => removeCredential(token, credentialId), 'Removed.');

    const fieldLabel = isMisc ? (row.requiredIdentifierLabel || 'Identifier') : row.secretLabel;

    return (
        <Box sx={{ p: 2, borderTop: first ? 'none' : `1px solid ${BORDER}` }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                {/* state */}
                <Box sx={{ minWidth: 120 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>{row.stateName}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: SLATE_400 }}>{row.postalAbbv} · {row.filingSystemLabel}</Typography>
                    <StatusChip approved={approved} hasValue={hasValue}
                        inherits={inheritsDefault} inheritedApproved={defaultApproved} />
                </Box>

                {/* fields */}
                <Box sx={{ flex: 1, minWidth: 260 }}>
                    {/* Inheriting the SLIP+ (system) default — collapsed until the admin overrides. */}
                    {inheritsDefault && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '13px', color: SLATE_500 }}>
                                    Using default{defaultUsername ? <span style={{ color: SLATE_400 }}> ({defaultUsername})</span> : ''}
                                </Typography>
                                {canManage && (
                                    <Button size="small" onClick={() => setOverriding(true)} disabled={busy}
                                        sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, minWidth: 0 }}>Override</Button>
                                )}
                            </Box>
                            {/* This state's health IS the default's health — same credential, one verdict.
                                Its approval is the DEFAULT's approval too, so the blocking form of the
                                chip keys on that rather than on this row. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                                <VerifiedChip at={lastGoodAt} approved={defaultApproved} />
                                <Typography sx={{ fontSize: '11px', color: SLATE_400 }}>
                                    Tested on the default above — one test covers every state using it.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Client-scope: choose how this client authenticates — an API key or a linked login. */}
                    {!isMisc && !inheritsDefault && (
                        <ToggleButtonGroup
                            size="small" exclusive value={mode} disabled={!canManage || busy}
                            onChange={(_e, v) => v && setMode(v)} sx={{ mb: 1.25 }}
                        >
                            <ToggleButton value="link" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>Linked login</ToggleButton>
                            <ToggleButton value="key" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>{row.secretLabel}</ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {/* ── Linked-login mode ── */}
                    {!isMisc && !inheritsDefault && mode === 'link' && (
                        <Box>
                            {hasLinked && !replacing ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: SLATE_400 }} />
                                    <Typography sx={{ fontSize: '13px', color: SLATE_500 }}>
                                        Linked to {row.linkedUsername || 'a State Login'}{row.linkedLicensee ? ` (${row.linkedLicensee})` : ''}
                                    </Typography>
                                    {canManage && (
                                        <Button size="small" onClick={() => setReplacing(true)} disabled={busy}
                                            sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, minWidth: 0 }}>Change</Button>
                                    )}
                                </Box>
                            ) : (
                                <TextField
                                    select fullWidth size="small" label="Link a State Login"
                                    value={selectedLoginId === '' ? '' : String(selectedLoginId)}
                                    disabled={!canManage || busy}
                                    onChange={(e) => setSelectedLoginId(e.target.value === '' ? '' : Number(e.target.value))}
                                    helperText={options && options.length === 0
                                        ? 'No State Logins on file for this client and state — add one on the State Logins screen first.'
                                        : 'Username comes from State Logins; the password stays there and is never copied.'}
                                    FormHelperTextProps={{ sx: { fontSize: '11px', color: SLATE_400 } }}
                                >
                                    {(options || []).map((o) => (
                                        <MenuItem key={o.stateLoginsId} value={String(o.stateLoginsId)}>
                                            {o.username}{o.licensee ? ` — ${o.licensee}` : ''}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            {/* Optional separate API key: some logins need a filing key beyond the
                                username/password (2026-07-24). Stored encrypted; the username still
                                comes from the linked login. Leave blank if the login is enough. */}
                            {(!hasLinked || replacing) && (
                                <TextField
                                    fullWidth size="small" type="password" label="Separate API key (optional)"
                                    value={secret} disabled={!canManage || busy}
                                    onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" sx={{ mt: 1.25 }}
                                    helperText="Only if this login files with an API key separate from its password. Encrypted; never shown again."
                                    FormHelperTextProps={{ sx: { fontSize: '11px', color: SLATE_400 } }}
                                />
                            )}
                        </Box>
                    )}

                    {/* ── API-key mode ── */}
                    {(isMisc || (!inheritsDefault && mode === 'key')) && (<>
                    {!isMisc && row.usesUsername && (
                        <TextField
                            fullWidth size="small" label="Username" value={username} disabled={!canManage || busy}
                            onChange={(e) => setUsername(e.target.value)} sx={{ mb: 1.25 }}
                            autoComplete="off"
                        />
                    )}

                    {(isMisc ? row.identHasValue : hasApiKey) && !replacing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: SLATE_400 }} />
                            <Typography sx={{ fontSize: '13px', color: SLATE_500 }}>
                                {fieldLabel} set{setAt ? ` on ${fmtDate(setAt)}` : ''}. Value hidden.
                            </Typography>
                            {canManage && (
                                <Button size="small" onClick={() => setReplacing(true)} disabled={busy}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, minWidth: 0 }}>
                                    Replace
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <TextField
                            fullWidth size="small" type="password" label={(isMisc ? row.identHasValue : hasApiKey) ? `New ${fieldLabel}` : `Enter ${fieldLabel}`}
                            value={secret} disabled={!canManage || busy}
                            onChange={(e) => setSecret(e.target.value)}
                            autoComplete="new-password"
                            helperText={'Encrypted at rest; never shown again once saved.'}
                            FormHelperTextProps={{ sx: { fontSize: '11px', color: SLATE_400 } }}
                        />
                    )}
                    </>)}

                    {/* Per-broker identifier (CA/TX broker number / filer id), shown ALONGSIDE the login
                        for a Client-scope state that requires one (post-2026-07-24). */}
                    {!isMisc && row.requiredIdentifierLabel && (
                        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px dashed ${BORDER}` }}>
                            <IdentifierField row={row} token={token} partnersId={partnersId} canManage={canManage} onChanged={onChanged} />
                        </Box>
                    )}

                    {/* MISC shared-login context line (legacy shared-login states only) */}
                    {isMisc && (
                        <Box sx={{ mt: 1 }}>
                            {row.miscHasSecret ? (
                                <Typography sx={{ fontSize: '11.5px', color: row.miscApproved ? SLATE_500 : AMBER }}>
                                    Files under the MISC {row.filingSystemLabel} login{row.miscUsername ? ` (${row.miscUsername})` : ''}
                                    {row.miscApproved ? ', approved.' : ' — MISC login not yet approved.'}
                                </Typography>
                            ) : (
                                <Typography sx={{ fontSize: '11.5px', color: RED }}>
                                    No MISC {row.filingSystemLabel} login on file — filings for this state will not transmit until it is added.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {approved && approvedByName && (
                        <Typography sx={{ fontSize: '11.5px', color: SLATE_400, mt: 0.75 }}>
                            Approved by {approvedByName}{lastUsedAt ? ` · last used ${fmtDate(lastUsedAt)}` : ''}
                        </Typography>
                    )}
                    {/* Connection health — deliberately separate from the Approved chip above, and seeded
                        from the stored last-known-good so it survives a reload. A failure never clears it. */}
                    {!inheritsDefault && (canVerify || lastGoodAt || lastOutcome || verifyNote) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                            <VerifiedChip at={lastGoodAt} approved={!!approved} />
                            {lastOutcome && (
                                <Typography sx={{ fontSize: '11px', color: SLATE_400 }}>
                                    {lastOutcome.host ? `${lastOutcome.host} · ` : ''}checked {fmtTime(lastOutcome.checkedAt)}
                                </Typography>
                            )}
                            {failure && (
                                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, width: '100%', color: RED }}>
                                    Test failed{failure.message ? ` — ${failure.message}` : '.'}
                                    {lastGoodAt ? ' The last successful check above still stands.' : ''}
                                </Typography>
                            )}
                            {!failure && lastOutcome?.message && (
                                <Typography sx={{ fontSize: '11.5px', color: SLATE_500, width: '100%' }}>
                                    {lastOutcome.message}
                                </Typography>
                            )}
                            {verifyNote && (
                                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, width: '100%', color: verifyNote.tone === 'warn' ? AMBER : RED }}>
                                    {verifyNote.text}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {msg && (
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, mt: 0.75, color: msg.tone === 'ok' ? GREEN : RED }}>
                            {msg.text}
                        </Typography>
                    )}
                </Box>

                {/* actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 132, alignItems: 'stretch' }}>
                    <Button variant="contained" size="small" onClick={handleSave} disabled={!canSave}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>
                        {busy ? 'Working…' : (hasValue ? 'Save change' : 'Save')}
                    </Button>

                    {canVerify && (
                        <Tooltip title={cooldownLeft > 0
                            ? 'Recently tested — the filing system limits how often this can be checked.'
                            : 'Authenticate the stored credential against the filing system. Separate from approval.'}>
                            <span>
                                <Button
                                    variant="outlined" size="small" onClick={runVerify}
                                    disabled={busy || verifying || cooldownLeft > 0}
                                    startIcon={verifying ? <CircularProgress size={12} sx={{ color: SLATE_400 }} /> : undefined}
                                    sx={{ textTransform: 'none', fontWeight: 600, width: '100%', color: SLATE_500, borderColor: BORDER, '&:hover': { borderColor: SLATE_400, backgroundColor: '#eef1f5' } }}>
                                    {verifying ? 'Testing…' : (cooldownLeft > 0 ? `Retry in ${cooldownLeft}s` : 'Test connection')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}

                    {hasValue && !approved && (
                        <Button variant="outlined" size="small" startIcon={<CheckCircleOutlineIcon />} onClick={handleApprove}
                            disabled={!canManage || busy}
                            sx={{ textTransform: 'none', fontWeight: 600, color: GREEN, borderColor: GREEN, '&:hover': { borderColor: GREEN, backgroundColor: '#e5f2eb' } }}>
                            Approve
                        </Button>
                    )}
                    {approved && (
                        <Button variant="outlined" size="small" startIcon={<BlockOutlinedIcon />} onClick={handleRevoke}
                            disabled={!canManage || busy}
                            sx={{ textTransform: 'none', fontWeight: 600, color: AMBER, borderColor: AMBER, '&:hover': { borderColor: AMBER, backgroundColor: '#f6eddb' } }}>
                            Revoke
                        </Button>
                    )}
                    {hasValue && (confirmRemove ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button variant="contained" size="small" onClick={handleRemove} disabled={busy}
                                sx={{ textTransform: 'none', fontWeight: 600, flex: 1, backgroundColor: RED, '&:hover': { backgroundColor: '#9e352d' } }}>
                                Confirm
                            </Button>
                            <Button size="small" onClick={() => setConfirmRemove(false)} disabled={busy}
                                sx={{ textTransform: 'none', color: SLATE_500 }}>
                                Cancel
                            </Button>
                        </Box>
                    ) : (
                        <Tooltip title="Deactivate this credential. A replacement must be re-approved before it can file.">
                            <span>
                                <Button variant="text" size="small" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmRemove(true)}
                                    disabled={!canManage || busy}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: RED, width: '100%' }}>
                                    Remove
                                </Button>
                            </span>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

/** The system-level default login (SLIP+): one login that covers all of a system's states unless a
 *  state overrides it. Reads the client's logins for the system (username only) and lets an admin set,
 *  approve, or clear the default. */
function SystemDefault({ token, partnersId, canManage, filingSystem, systemLabel, def, onChanged, verifiedAt, probeState, coverage }: {
    token?: string; partnersId: number; canManage: boolean; filingSystem: string; systemLabel: string;
    def?: FilingLoginDefault; onChanged: () => void;
    /** The default's persisted last-known-good, if the backend surfaces one yet (null when it does not). */
    verifiedAt: string | null;
    /** A state that INHERITS this default, used to address the state-keyed verify endpoint. Null when no
     *  state inherits it (every state overrode) — then there is nothing here to test. */
    probeState: string | null;
    /** The states this one login covers, for the button's tooltip. */
    coverage: string;
}) {
    const [options, setOptions] = useState<StateLoginOption[] | null>(null);
    const [editing, setEditing] = useState(!def);
    const [selected, setSelected] = useState<number | ''>('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

    useEffect(() => {
        if (!editing || options !== null || !token) return;
        let cancelled = false;
        (async () => { const o = await getSystemLoginOptions(token, partnersId, filingSystem); if (!cancelled) setOptions(o); })();
        return () => { cancelled = true; };
    }, [editing, options, token, partnersId, filingSystem]);

    // ── Connection test, at the level the credential actually lives at ─────────────────────────────
    // One SLIP+ login covers every state in this group, so ONE test answers for all of them. Same rules
    // as a per-state test: click-only (never on render), success moves the last-known-good, failure is
    // reported without erasing it, cooldown is a normal outcome that leaves the verdict standing.
    const [verifying, setVerifying] = useState(false);
    const [freshGoodAt, setFreshGoodAt] = useState<string | null>(null);
    const [lastOutcome, setLastOutcome] = useState<VerifyCredentialData | null>(null);
    const [verifyNote, setVerifyNote] = useState<{ tone: 'warn' | 'err'; text: string } | null>(null);
    const [cooldownLeft, setCooldownLeft] = useState(0);
    const [mutatedSinceLoad, setMutatedSinceLoad] = useState(false);
    useEffect(() => { setMutatedSinceLoad(false); }, [def]);

    const lastGoodAt = freshGoodAt ?? (mutatedSinceLoad ? null : verifiedAt);
    const failure = lastOutcome && lastOutcome.status !== 'connected' ? lastOutcome : null;
    const canVerify = !!def && !!probeState;

    useEffect(() => {
        if (cooldownLeft <= 0) return;
        const t = setTimeout(() => setCooldownLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldownLeft]);

    const runVerify = useCallback(async () => {
        if (!probeState) return;
        setVerifying(true); setVerifyNote(null);
        const res = await verifyCredential(token, partnersId, probeState);
        setVerifying(false);
        if (!res.ok || !res.data) {
            setVerifyNote({ tone: 'err', text: res.message || 'Could not reach the filing system — try again.' });
            return;
        }
        if (res.data.status === 'cooldown') {
            setVerifyNote({ tone: 'warn', text: res.data.message });
            setCooldownLeft(Math.max(0, Math.ceil(res.data.retryAfterSeconds ?? 0)));
            return;
        }
        setLastOutcome(res.data);
        if (res.data.status === 'connected') setFreshGoodAt(res.data.checkedAt);
    }, [token, partnersId, probeState]);

    const run = async (fn: () => Promise<{ ok: boolean; message?: string }>, okText: string, after?: () => void) => {
        setBusy(true); setMsg(null);
        const res = await fn(); setBusy(false);
        if (res.ok) {
            setSelected(''); setEditing(false); setMsg({ tone: 'ok', text: okText });
            // The stored verdict described the PREVIOUS default login.
            setFreshGoodAt(null); setLastOutcome(null); setVerifyNote(null); setMutatedSinceLoad(true);
            onChanged();
            if (after) after();
        } else setMsg({ tone: 'err', text: res.message || 'Request failed — try again.' });
    };
    const save = () => selected !== '' && run(() => setFilingLoginDefault(token, { partnersId, filingSystem, stateLoginsId: Number(selected) }), 'Default saved. Awaiting approval.');
    // Approving is the one place a test fires without its own click: once a login is cleared to file, an
    // admin should immediately see whether it actually authenticates.
    const approve = () => def && run(() => approveFilingLoginDefault(token, def.partnerFilingLoginDefaultId), 'Default approved.', canVerify ? runVerify : undefined);
    const remove = () => def && run(() => removeFilingLoginDefault(token, def.partnerFilingLoginDefaultId), 'Default cleared.');

    return (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff', p: 1.75, mb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: SLATE_500, textTransform: 'uppercase', letterSpacing: '.05em', minWidth: 96 }}>
                    {systemLabel} default
                </Typography>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    {def && !editing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: SLATE_400 }} />
                            <Typography sx={{ fontSize: '13px', color: SLATE_500 }}>
                                {def.loginUsername || 'a State Login'}{def.loginLicensee ? ` (${def.loginLicensee})` : ''}
                            </Typography>
                            {canManage && <Button size="small" onClick={() => setEditing(true)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, minWidth: 0 }}>Change</Button>}
                        </Box>
                    ) : (
                        <TextField
                            select fullWidth size="small" label="Default login for all these states"
                            value={selected === '' ? '' : String(selected)} disabled={!canManage || busy}
                            onChange={(e) => setSelected(e.target.value === '' ? '' : Number(e.target.value))}
                            helperText={options && options.length === 0 ? 'No State Logins on file for this client in this system.' : 'From this client’s State Logins; the password stays there.'}
                            FormHelperTextProps={{ sx: { fontSize: '11px', color: SLATE_400 } }}
                        >
                            {(options || []).map((o) => (
                                <MenuItem key={o.stateLoginsId} value={String(o.stateLoginsId)}>{o.username}{o.licensee ? ` — ${o.licensee}` : ''}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {msg && <Typography sx={{ fontSize: '12px', fontWeight: 600, mt: 0.5, color: msg.tone === 'ok' ? GREEN : RED }}>{msg.text}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Chip label={def ? (def.approvedForSubmit ? 'Approved' : 'Awaiting approval') : 'Not set'} size="small"
                        sx={{ height: 20, fontSize: '10.5px', fontWeight: 700,
                            color: def ? (def.approvedForSubmit ? GREEN : AMBER) : AMBER,
                            backgroundColor: def && def.approvedForSubmit ? '#e5f2eb' : '#f6eddb' }} />
                    {def && <VerifiedChip at={lastGoodAt} approved={!!def.approvedForSubmit} />}
                    {canVerify && (
                        <Tooltip title={cooldownLeft > 0
                            ? 'Recently tested — the filing system limits how often this can be checked.'
                            : `Authenticate this login against ${systemLabel}. One test covers ${coverage}.`}>
                            <span>
                                <Button
                                    variant="outlined" size="small" onClick={runVerify}
                                    disabled={busy || verifying || cooldownLeft > 0}
                                    startIcon={verifying ? <CircularProgress size={12} sx={{ color: SLATE_400 }} /> : undefined}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: SLATE_500, borderColor: BORDER, '&:hover': { borderColor: SLATE_400, backgroundColor: '#eef1f5' } }}>
                                    {verifying ? 'Testing…' : (cooldownLeft > 0 ? `Retry in ${cooldownLeft}s` : 'Test connection')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {selected !== '' && <Button size="small" variant="contained" onClick={save} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>Save</Button>}
                    {def && !def.approvedForSubmit && <Button size="small" onClick={approve} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 700, color: GREEN, minWidth: 0 }}>Approve</Button>}
                    {def && <Button size="small" onClick={remove} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 700, color: RED, minWidth: 0 }}>Clear</Button>}
                </Box>
                {/* Test detail on its own line so it can never crowd the controls above. */}
                {(lastOutcome || verifyNote) && (
                    <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {lastOutcome && (
                            <Typography sx={{ fontSize: '11px', color: SLATE_400 }}>
                                {lastOutcome.host ? `${lastOutcome.host} · ` : ''}checked {fmtTime(lastOutcome.checkedAt)}
                            </Typography>
                        )}
                        {failure && (
                            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, width: '100%', color: RED }}>
                                Test failed{failure.message ? ` — ${failure.message}` : '.'}
                                {lastGoodAt ? ' The last successful check above still stands.' : ''}
                            </Typography>
                        )}
                        {!failure && lastOutcome?.message && (
                            <Typography sx={{ fontSize: '11.5px', color: SLATE_500, width: '100%' }}>{lastOutcome.message}</Typography>
                        )}
                        {verifyNote && (
                            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, width: '100%', color: verifyNote.tone === 'warn' ? AMBER : RED }}>
                                {verifyNote.text}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

/** The per-broker identifier (SLA broker number / SLTX filer id) — its own credential row, saved and
 *  approved independently of the login. Encrypted at rest, so the value is never shown back; an empty
 *  one is flagged in amber so gaps are obvious. */
function IdentifierField({ row, token, partnersId, canManage, onChanged }: {
    row: StateCredentialRow; token?: string; partnersId: number; canManage: boolean; onChanged: () => void;
}) {
    const id = row.identifierId;
    const has = row.identHasValue;
    const [val, setVal] = useState('');
    const [replacing, setReplacing] = useState(!has);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const label = row.requiredIdentifierLabel || 'Identifier';

    const run = async (fn: () => Promise<{ ok: boolean; message?: string }>, okText: string) => {
        setBusy(true); setMsg(null);
        const res = await fn();
        setBusy(false);
        if (res.ok) { setVal(''); setReplacing(false); setConfirmRemove(false); setMsg({ tone: 'ok', text: okText }); onChanged(); }
        else setMsg({ tone: 'err', text: res.message || 'Request failed — try again.' });
    };
    const save = () => run(() => saveCredential(token, { partnersId, state: row.postalAbbv, value: val }), 'Saved. Awaiting approval.');
    const approve = () => id && run(() => approveCredential(token, id), 'Approved.');
    const revoke = () => id && run(() => revokeCredential(token, id), 'Approval revoked.');
    const remove = () => id && run(() => removeCredential(token, id), 'Removed.');

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: SLATE_500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</Typography>
                {has && !replacing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: SLATE_500 }}>{label} set. Value hidden.</Typography>
                        {canManage && <Button size="small" onClick={() => setReplacing(true)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, minWidth: 0 }}>Replace</Button>}
                    </Box>
                ) : (
                    <TextField
                        fullWidth size="small" label={`Enter ${label}`} value={val} disabled={!canManage || busy}
                        onChange={(e) => setVal(e.target.value)} autoComplete="off"
                        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: AMBER, borderStyle: 'dashed' } }}
                    />
                )}
                {msg && <Typography sx={{ fontSize: '12px', fontWeight: 600, mt: 0.5, color: msg.tone === 'ok' ? GREEN : RED }}>{msg.text}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Chip label={row.identApproved ? 'Approved' : has ? 'Awaiting approval' : 'Not set'} size="small"
                    sx={{ height: 20, fontSize: '10.5px', fontWeight: 700,
                        color: row.identApproved ? GREEN : AMBER,
                        backgroundColor: row.identApproved ? '#e5f2eb' : '#f6eddb' }} />
                {(val.trim() !== '' ) && (
                    <Button size="small" variant="contained" onClick={save} disabled={!canManage || busy}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>Save</Button>
                )}
                {has && !row.identApproved && <Button size="small" onClick={approve} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 700, color: GREEN, minWidth: 0 }}>Approve</Button>}
                {row.identApproved && <Button size="small" onClick={revoke} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 700, color: AMBER, minWidth: 0 }}>Revoke</Button>}
                {has && (confirmRemove
                    ? <><Button size="small" onClick={remove} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: RED, minWidth: 0 }}>Confirm</Button>
                        <Button size="small" onClick={() => setConfirmRemove(false)} disabled={busy} sx={{ textTransform: 'none', color: SLATE_500, minWidth: 0 }}>Cancel</Button></>
                    : <Button size="small" onClick={() => setConfirmRemove(true)} disabled={!canManage || busy} sx={{ textTransform: 'none', fontWeight: 700, color: RED, minWidth: 0 }}>Remove</Button>)}
            </Box>
        </Box>
    );
}

/**
 * The last-known-good marker. Outlined on purpose so it never reads as a second Approved chip: approval
 * is a human sign-off, this is a machine reachability check, and the two move independently.
 *
 * It ALWAYS carries a date. A bare green "verified" chip is a claim the data does not support — the
 * server stamps LastVerifiedAt only on success and never clears it, so a credential that broke months
 * ago still has one. Showing the age is what makes the chip honest, and anything past
 * VERIFY_STALE_DAYS goes amber so "verified in April" cannot pass for "verified today".
 */
function VerifiedChip({ at, approved = false }: { at: string | null; approved?: boolean }) {
    if (!at) {
        // NEVER VERIFIED NOW BLOCKS FILING. credentialResolver refuses a login that has never
        // authenticated against its filing system, so on an otherwise-approved credential this is the
        // one thing standing between the client and filing, and a neutral grey "Not tested yet" would
        // understate it — Ally's SLIP+ default was approved, active, never tested, and transmitted
        // anyway (2026-07-28).
        //
        // Only RED once approved: on an unapproved row, approval is the nearer obstacle, and two red
        // chips side by side would not tell an admin which one to fix first.
        return (
            <Tooltip title={approved
                ? 'This credential has never authenticated against the filing system, so filing is blocked. Run Test connection, and it can file as soon as that succeeds.'
                : 'This credential has not been authenticated against the filing system yet.'}>
                <Chip label={approved ? 'Cannot file: not tested' : 'Not tested yet'} size="small" variant="outlined"
                    sx={{ height: 20, fontSize: '10.5px', fontWeight: 700,
                        color: approved ? RED : SLATE_400, borderColor: approved ? RED : BORDER, backgroundColor: 'transparent' }} />
            </Tooltip>
        );
    }
    const age = daysSince(at);
    const stale = age !== null && age > VERIFY_STALE_DAYS;
    const color = stale ? AMBER : GREEN;
    const when = new Date(at);
    const exact = Number.isNaN(when.getTime()) ? at : when.toLocaleString();
    return (
        <Tooltip title={`Last successful authentication: ${exact}.${stale ? ' That was a while ago — re-test before relying on it.' : ''}`}>
            <Chip label={fmtVerifiedOn(at)} size="small" variant="outlined"
                sx={{ height: 20, fontSize: '10.5px', fontWeight: 700, color, borderColor: color, backgroundColor: 'transparent' }} />
        </Tooltip>
    );
}

function StatusChip({ approved, hasValue, inherits, inheritedApproved }: {
    approved: boolean; hasValue: boolean; inherits?: boolean; inheritedApproved?: boolean;
}) {
    // A state covered by an approved system default IS configured. Labelling it "Not set" was what made
    // a fully-working SLIP+ client read as unconfigured on this screen.
    const [label, color, bg] = inherits
        ? (inheritedApproved
            ? ['Using default', GREEN, '#e5f2eb']
            : ['Default awaiting approval', AMBER, '#f6eddb'])
        : approved
            ? ['Approved', GREEN, '#e5f2eb']
            : hasValue
                ? ['Awaiting approval', AMBER, '#f6eddb']
                : ['Not set', SLATE_400, '#eef1f5'];
    return <Chip label={label} size="small" sx={{ mt: 0.75, height: 20, fontSize: '10.5px', fontWeight: 700, color, backgroundColor: bg }} />;
}

export default StateFilingCredentialsPanel;
